import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Avatar from './Avatar';
import { useModal } from '../context/ModalContext';
import '../styles/ProfilePanel.css';

const ProfilePanel = ({ user, onUserUpdate }) => {
  const modal = useModal();
  const [profileData, setProfileData] = useState({
    name: user.name || '',
    notification_email: user.invitation_email || user.email || ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Slack integration state
  const [slackConnected, setSlackConnected] = useState(false);
  const [slackLoading, setSlackLoading] = useState(true);

  useEffect(() => {
    checkSlackStatus();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('slack_connected') === 'true') {
      if (modal && modal.success) {
        modal.success('Slack connected successfully!');
      }
      window.history.replaceState({}, '', window.location.pathname);
    } else if (urlParams.get('error') === 'slack_connection_failed') {
      if (modal && modal.error) {
        modal.error('Failed to connect Slack.\nPlease try again.');
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [modal]);

  const handleChange = (field, value) => {
    setProfileData({...profileData, [field]: value});
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post('/api/user/update-profile', profileData);
      modal.success('Profile updated successfully');
      setHasChanges(false);

      // Refresh user data without page reload
      if (onUserUpdate) {
        await onUserUpdate();
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      modal.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const checkSlackStatus = async () => {
    try {
      const response = await axios.get('/api/slack/status');
      setSlackConnected(response.data.connected);
    } catch (error) {
      console.error('Failed to check Slack status:', error);
    } finally {
      setSlackLoading(false);
    }
  };

  const handleConnectSlack = () => {
    window.location.href = '/auth/slack';
  };

  const handleDisconnectSlack = async () => {
    if (!window.confirm('Are you sure you want to disconnect Slack notifications?')) {
      return;
    }

    try {
      await axios.post('/api/slack/disconnect');
      setSlackConnected(false);
      modal.success('Slack disconnected successfully');
    } catch (error) {
      console.error('Failed to disconnect Slack:', error);
      modal.error('Failed to disconnect Slack');
    }
  };

  return (
    <div className="profile-panel">
      {/* Profile Information */}
      <div className="profile-section">
        <div className="section-header">
          <h3>Profile Information</h3>
          <button
            onClick={handleSave}
            className={`save-btn ${hasChanges ? 'active' : 'disabled'}`}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
        <div className="profile-info-grid">
          <div className="profile-picture-container">
            <Avatar user={user} size="xl" className="profile-picture" />
          </div>
          <div className="info-row">
            <label>Name</label>
            <input
              type="text"
              value={profileData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Enter your display name"
              className="notification-email-input"
              autoComplete="off"
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              data-form-type="other"
            />
          </div>
          <div className="info-row">
            <label>Notification Email</label>
            <input
              type="email"
              value={profileData.notification_email}
              onChange={(e) => handleChange('notification_email', e.target.value)}
              placeholder="Enter email for notifications"
              className="notification-email-input"
              autoComplete="off"
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              data-form-type="other"
            />
          </div>

          {/* Slack Connection */}
          <div className="info-row slack-integration">
            <label>Slack Connection</label>
            {slackLoading ? (
              <div className="slack-loading">Loading...</div>
            ) : slackConnected ? (
              <button onClick={handleDisconnectSlack} className="slack-disconnect-btn">
                Disconnect
              </button>
            ) : (
              <button onClick={handleConnectSlack} className="slack-connect-btn">
                Connect Slack
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePanel;
